<script setup lang="ts">
  import { ref, watch } from 'vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import { useReservationGuestLookup } from '@/composition/reservations/use-reservation-guest-lookup';
  import { formatReservationPhoneInput } from '@/domain/reservations/reservation-phone';
  import { isNoShowRisk } from '@/domain/reservations/reservation-status';
  import type { IReservationGuest } from '@/interfaces/IReservationGuest';
  import type { IReservationGuestDraft } from './reservation-form.interfaces';
  const props = withDefaults(
    defineProps<{
      value: IReservationGuestDraft;
      restaurantId: number;
      branchId: number;
      country: string;
      disabled?: boolean;
      nameError?: string;
      phoneError?: string;
    }>(),
    { disabled: false, nameError: undefined, phoneError: undefined }
  );
  const emit = defineEmits<{
    (event: 'input', value: IReservationGuestDraft): void;
  }>();
  const {
    query,
    matches,
    loading,
    failed,
    ready,
    search,
  } = useReservationGuestLookup(() => ({
    restaurantId: props.restaurantId,
    branchId: props.branchId,
  }));
  const phoneInput = ref(props.value.phone);
  watch(
    () => props.value.phone,
    (value) => {
      if (!/\p{L}/u.test(phoneInput.value)) phoneInput.value = value;
    }
  );
  function phone(value: string, open: boolean, toggle: () => void) {
    const isName = /\p{L}/u.test(value);
    phoneInput.value = isName
      ? value
      : formatReservationPhoneInput(value, props.country);
    query.value = phoneInput.value;
    emit('input', {
      ...props.value,
      customerId: null,
      phone: isName ? '' : phoneInput.value,
    });
    if (ready.value && !open) toggle();
  }
  function name(value: string, open: boolean, toggle: () => void) {
    emit('input', { ...props.value, name: value });
    query.value = value;
    if (ready.value && !open) toggle();
  }
  function choose(guest: IReservationGuest, close: () => void) {
    phoneInput.value = guest.phoneE164 ?? '';
    query.value = '';
    emit('input', {
      customerId: guest.customerId,
      name: guest.name,
      phone: guest.phoneE164 ?? '',
      email: guest.email ?? '',
    });
    close();
  }
</script>
<template>
  <div class="reservation-guest-lookup">
    <PosUiPopover class="lookup-popover" placement="bottom" :disabled="disabled"
      ><template #trigger="{ open, toggle }"
        ><div class="guest-fields">
          <PosUiField
            :label="$t('reservations.form.phone')"
            input-id="reservation-guest-phone"
            :error="phoneError"
            ><PosUiInput
              id="reservation-guest-phone"
              :value="phoneInput"
              icon="phone"
              inputmode="tel"
              :maxlength="64"
              :disabled="disabled"
              :placeholder="$t('reservations.form.phonePlaceholder')"
              @input="phone($event, open, toggle)" /></PosUiField
          ><PosUiField
            :label="$t('reservations.form.name')"
            input-id="reservation-guest-name"
            :error="nameError"
            required
            ><PosUiInput
              id="reservation-guest-name"
              :value="value.name"
              :maxlength="128"
              :disabled="disabled"
              @input="name($event, open, toggle)"
          /></PosUiField></div></template
      ><template #default="{ close }"
        ><div class="lookup-results">
          <template v-if="loading"
            ><PosUiSkeleton
              v-for="index in 3"
              :key="index"
              class="guest-skeleton" /></template
          ><template v-else-if="failed"
            ><p role="alert">{{ $t('reservations.page.loadError') }}</p>
            <PosUiButton color="neutral" variant="outline" @click="search">{{
              $t('reservations.page.retry')
            }}</PosUiButton></template
          ><template v-else-if="matches.length"
            ><button
              v-for="guest in matches"
              :key="guest.customerId"
              type="button"
              class="guest-choice"
              @click="choose(guest, close)"
            >
              <PosUiBadge
                v-if="isNoShowRisk(guest)"
                class="guest-risk"
                color="error"
                variant="soft"
                :title="
                  $t('reservations.detail.noShowCount', {
                    count: guest.noShowsCount,
                  })
                "
                >{{ $t('reservations.detail.noShowRisk') }}</PosUiBadge
              >
              <strong>{{ guest.name }}</strong
              ><span>{{ guest.phoneE164 || guest.email }}</span
              ><small
                >{{
                  $t('reservations.detail.visits', { count: guest.visitsCount })
                }}
                ·
                {{
                  $t('reservations.detail.noShows', {
                    count: guest.noShowsCount,
                  })
                }}</small
              >
            </button></template
          >
          <p v-else>{{ $t('reservations.form.newGuest') }}</p>
        </div></template
      ></PosUiPopover
    >
    <PosUiBadge
      class="guest-kind"
      :color="value.customerId ? 'info' : 'neutral'"
      >{{
        $t(
          value.customerId
            ? 'reservations.form.knownGuest'
            : 'reservations.form.newGuest'
        )
      }}</PosUiBadge
    >
  </div>
</template>
<style scoped lang="scss">
  .reservation-guest-lookup {
    @apply tw-flex tw-flex-col tw-gap-2;
    .lookup-popover {
      @apply tw-w-full;
    }
    .guest-fields {
      @apply tw-grid tw-w-full tw-gap-3 sm:tw-grid-cols-2;
    }
    .guest-kind {
      @apply tw-self-start;
    }
    .lookup-results {
      @apply tw-flex tw-flex-col tw-gap-2;
      p {
        @apply tw-m-0 tw-text-sm tw-text-neutral-500;
      }
    }
    .guest-choice {
      @apply tw-flex tw-min-h-14 tw-flex-col tw-gap-1 tw-rounded-lg tw-px-3 tw-py-2 tw-text-left hover:tw-bg-neutral-100;
      strong {
        @apply tw-text-sm tw-font-semibold tw-text-neutral-900;
      }
      span:not(.guest-risk),
      small {
        @apply tw-text-xs tw-text-neutral-500;
      }
      .guest-risk {
        @apply tw-self-start;
      }
    }
    .guest-skeleton {
      @apply tw-h-14;
    }
  }
</style>
